export type ScheduleArtifactCourse = {
  baseId: string;
  courseName: string;
  section?: string;
  campus?: string;
  credits?: string | number;
  instructors?: Array<string | { name?: string }>;
  isOpen?: boolean;
  meetingTimes: Array<{ day?: string; startMinute?: number; endMinute?: number; location?: string; campus?: string; mode?: string }>;
};

export type ScheduleArtifactOption = {
  id: string;
  qualityScore?: number;
  courses: ScheduleArtifactCourse[];
};

export type ScheduleArtifact = {
  artifactId: string;
  artifactType: 'scarlet.schedule-options.v1';
  title: string;
  summary: string;
  semester?: string;
  optionIds: string[];
  options: ScheduleArtifactOption[];
};

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function parseCourse(value: unknown): ScheduleArtifactCourse | null {
  const course = record(value);
  if (!course || typeof course.baseId !== 'string' || typeof course.courseName !== 'string') return null;
  const meetingTimes = Array.isArray(course.meetingTimes)
    ? course.meetingTimes.flatMap((meeting) => {
      const item = record(meeting);
      return item ? [{
        day: typeof item.day === 'string' ? item.day : undefined,
        startMinute: typeof item.startMinute === 'number' ? item.startMinute : undefined,
        endMinute: typeof item.endMinute === 'number' ? item.endMinute : undefined,
        location: typeof item.location === 'string' ? item.location : undefined,
        campus: typeof item.campus === 'string' ? item.campus : undefined,
        mode: typeof item.mode === 'string' ? item.mode : undefined,
      }] : [];
    })
    : [];
  return {
    baseId: course.baseId,
    courseName: course.courseName,
    section: typeof course.section === 'string' ? course.section : undefined,
    campus: typeof course.campus === 'string' ? course.campus : undefined,
    credits: typeof course.credits === 'string' || typeof course.credits === 'number' ? course.credits : undefined,
    instructors: Array.isArray(course.instructors)
      ? course.instructors.filter((instructor): instructor is string | { name?: string } =>
          typeof instructor === 'string' || Boolean(instructor && typeof instructor === 'object' && !Array.isArray(instructor)),
        )
      : undefined,
    isOpen: typeof course.isOpen === 'boolean' ? course.isOpen : undefined,
    meetingTimes,
  };
}

function parseArtifactRecord(value: unknown): ScheduleArtifact | null {
  const artifact = record(value);
  if (!artifact || artifact.artifactType !== 'scarlet.schedule-options.v1') return null;
  if (typeof artifact.artifactId !== 'string' || typeof artifact.title !== 'string' || typeof artifact.summary !== 'string') return null;
  const optionIds = Array.isArray(artifact.optionIds)
    ? artifact.optionIds.filter((id): id is string => typeof id === 'string')
    : [];
  const rawOptions = Array.isArray(artifact.options) ? artifact.options : [];
  const options = rawOptions.flatMap((value, index) => {
    const option = record(value);
    if (!option) return [];
    const courses = Array.isArray(option.courses) ? option.courses.flatMap((course) => {
      const parsed = parseCourse(course);
      return parsed ? [parsed] : [];
    }) : [];
    return [{
      id: optionIds[index] ?? `option-${index + 1}`,
      qualityScore: typeof option.qualityScore === 'number' ? option.qualityScore : undefined,
      courses,
    }];
  });
  return {
    artifactId: artifact.artifactId,
    artifactType: artifact.artifactType,
    title: artifact.title,
    summary: artifact.summary,
    semester: typeof artifact.semester === 'string' ? artifact.semester : undefined,
    optionIds,
    options,
  };
}

export function parseScheduleArtifact(result: unknown): ScheduleArtifact | null {
  const root = record(result);
  const data = record(root?.data);
  return parseArtifactRecord(data?.artifact) ?? parseArtifactRecord(data);
}

export function formatMeetingTime(minutes?: number): string {
  if (minutes === undefined) return '';
  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour = hour24 % 12 || 12;
  return `${hour}:${String(minute).padStart(2, '0')} ${period}`;
}
